import type { Event } from "@sentry/core";

/**
 * Detects TypeErrors thrown by Facebook's in-app browser autofill bridge.
 *
 * The Facebook app (Android & iOS) injects autofill code that calls
 * `setContactAutofillValuesFromBridge`, which iterates over form elements and
 * accesses `.value` on each.  When an expected element is missing or undefined
 * the bridge throws a TypeError.  The entire stack trace lives in minified
 * `<anonymous>` code — there is nothing actionable in our application.
 *
 * To avoid false positives (the message "reading 'value'" is very generic),
 * we require **both**:
 *   1. A TypeError whose message mentions accessing `value` on undefined, AND
 *   2. The function name `setContactAutofillValuesFromBridge` in the stack frames.
 *
 * Browser TypeError formats for accessing `.value` on undefined:
 *   Chrome : "Cannot read properties of undefined (reading 'value')"
 *   Safari : "undefined is not an object (evaluating '…value')"
 *   Firefox: "can't access property 'value' of undefined"
 */
const FACEBOOK_BRIDGE_FN = "setContactAutofillValuesFromBridge";

export function isFacebookAutofillError(event: Event): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  return values.some((exceptionValue) => {
    // 1. Must be a TypeError about accessing 'value' on undefined.
    const isValueTypeError =
      exceptionValue.type === "TypeError" &&
      !!exceptionValue.value &&
      matchesValueOnUndefined(exceptionValue.value);

    if (!isValueTypeError) return false;

    // 2. Must have the Facebook autofill bridge function in the stack trace.
    const frames = exceptionValue.stacktrace?.frames;
    if (!frames?.length) return false;

    return frames.some(
      (frame) =>
        frame.function?.includes(FACEBOOK_BRIDGE_FN) ||
        frame.abs_path?.includes(FACEBOOK_BRIDGE_FN),
    );
  });
}

/**
 * Returns `true` when `message` looks like a TypeError about accessing
 * `value` on `undefined`, regardless of browser engine.
 */
function matchesValueOnUndefined(message: string): boolean {
  const lower = message.toLowerCase();
  if (!lower.includes("undefined")) return false;

  return (
    // Chrome: "Cannot read properties of undefined (reading 'value')"
    /reading\s+'value'/.test(lower) ||
    // Safari: "undefined is not an object (evaluating '….value')"
    /evaluating\s+'[^']*\.value'/.test(lower) ||
    // Firefox: "can't access property 'value' of undefined"
    /property\s+'value'/.test(lower)
  );
}

/**
 * Check whether a Sentry event is a third-party Google Maps error that should
 * be dropped.  The Google Maps Places JS SDK throws `InvalidValueError` when
 * it receives invalid input (e.g. an empty string).  The stack trace lives
 * entirely inside `maps-api-v3/` — there is nothing actionable in our code.
 *
 * We match on **both** the error type/message **and** the presence of
 * `maps-api-v3` frames so we don't accidentally suppress unrelated errors
 * that happen to share the same type name.
 */
export function isGoogleMapsError(event: Event): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  return values.some((exceptionValue) => {
    const isInvalidValueError =
      exceptionValue.type === "InvalidValueError" ||
      (exceptionValue.value?.includes("InvalidValueError") ?? false);

    if (!isInvalidValueError) return false;

    // Confirm the stack trace originates from Google Maps code.
    // If there's no stack trace we can't confirm the origin, so don't filter —
    // the root cause fix (empty string guard) handles most cases anyway.
    const frames = exceptionValue.stacktrace?.frames;
    if (!frames?.length) return false;

    return frames.some(
      (frame) =>
        frame.filename?.includes("maps-api-v3") ||
        frame.abs_path?.includes("maps-api-v3")
    );
  });
}
